"use client";

import { SentryFeatureTags, logException } from "@punchup/sentry";
import {
  ShowWithComedian,
  updateTixologiAvailabilityClient,
} from "@punchup/data/legacy";

import { VenuePagesRow } from "@punchup/data/models";
import dayjs from "@punchup/utils/dayjs";
import { getApiClient } from "@punchup/utils/apiRegistry";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

const INITIAL_SHOWS_LIMIT = 20;
const PAGE_SIZE = 20;

interface GetVenuePageShowsOptions {
  limit?: number;
  offset?: number;
  search?: string;
}

const getVenuePageShows = async (
  venuePageId: VenuePagesRow["id"],
  venueSlug: VenuePagesRow["slug"],
  options?: GetVenuePageShowsOptions
): Promise<ShowWithComedian[]> => {
  try {
    const { limit, offset, search } = options ?? {};
    let url = `/api/shows?venuePageId=${venuePageId}`;
    if (limit !== undefined) url += `&limit=${limit}`;
    if (offset !== undefined) url += `&offset=${offset}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;

    let shows = await getApiClient().get<ShowWithComedian[]>(url);
    shows = await updateTixologiAvailabilityClient(shows);

    // TODO: remove once we have a proper venue for the NCAC show
    if (venueSlug === "ncac") {
      shows = shows.filter(
        (show) => show.venue === "Stand Up For Free Speech Comedy Benefit"
      );
    }

    return [...shows].sort(
      (a, b) => dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf()
    );
  } catch (error) {
    logException(error as Error, {
      extra: {
        data: "Error fetching venue shows inside useVenuePageShows hook",
      },
      tags: { feature: SentryFeatureTags.VenuePages, venuePageId },
    });
    throw error;
  }
};

interface UseVenuePageShowsOptions {
  venuePageId: string;
  venueSlug: string;
  enabled?: boolean;
  searchTerm?: string;
}

interface UseVenuePageShowsResult {
  shows: ShowWithComedian[];
  searchResults: ShowWithComedian[];
  isSearching: boolean;
  next5Shows: ShowWithComedian[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  fetchMoreShows: () => Promise<void>;
  isLoadingMore: boolean;
  hasMore: boolean;
}

export function useVenuePageShows({
  venuePageId,
  venueSlug,
  enabled = true,
  searchTerm = "",
}: UseVenuePageShowsOptions): UseVenuePageShowsResult {
  const queryClient = useQueryClient();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const isLoadingMoreRef = useRef(false);

  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: venueShows = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["venueShows", venuePageId],
    queryFn: () =>
      getVenuePageShows(venuePageId, venueSlug, { limit: INITIAL_SHOWS_LIMIT }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  // Detect end-of-list from initial server-prefetched data
  useEffect(() => {
    if (
      !isLoading &&
      venueShows.length > 0 &&
      venueShows.length < INITIAL_SHOWS_LIMIT
    ) {
      setHasMore(false);
    }
  }, [isLoading, venueShows.length]);

  const shows = [...venueShows].sort(
    (a, b) => dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf()
  );

  const next5Shows = shows.slice(0, 5);

  const fetchMoreShows = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMore) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const currentShows =
        queryClient.getQueryData<ShowWithComedian[]>([
          "venueShows",
          venuePageId,
        ]) ?? [];

      const nextPage = await getVenuePageShows(venuePageId, venueSlug, {
        limit: PAGE_SIZE,
        offset: currentShows.length,
      });

      const merged = [...currentShows, ...nextPage].sort(
        (a, b) => dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf()
      );
      queryClient.setQueryData(["venueShows", venuePageId], merged);

      if (nextPage.length < PAGE_SIZE) {
        setHasMore(false);
      }
    } catch (error) {
      logException(error as Error, {
        extra: { data: "Error fetching more venue shows" },
        tags: { feature: SentryFeatureTags.VenuePages, venuePageId },
      });
      // Don't re-throw — keep existing shows, user can retry
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [venuePageId, venueSlug, queryClient, hasMore]);

  const { data: searchResults = [], isFetching: isFetchingSearch } = useQuery({
    queryKey: ["venueShows", venuePageId, "search", debouncedSearch],
    queryFn: () =>
      getVenuePageShows(venuePageId, venueSlug, { search: debouncedSearch }),
    enabled: enabled && debouncedSearch.length > 0,
    staleTime: 60_000,
  });

  // Treat the debounce window as "searching" so consumers can render a
  // loading state immediately while the user is still typing, not just
  // once the network request fires.
  const isAwaitingDebounce =
    searchTerm.length > 0 && searchTerm !== debouncedSearch;
  const isSearching = isAwaitingDebounce || isFetchingSearch;

  return {
    shows,
    searchResults,
    isSearching,
    next5Shows,
    isLoading,
    error: error as Error | null,
    refetch,
    fetchMoreShows,
    isLoadingMore,
    hasMore,
  };
}
